import React, { Component } from 'react';
import { inject } from 'mobx-react';
import { AGENT_CAPABILITIES } from '@kentik/ui-shared/synthetics/constants';
import { Box, Button, Dialog, Flex, Heading, Icon } from 'core/components';
import { Field, FormComponent, InputGroup, Select } from 'core/form/components';
import AgentSelectorFormBody from 'app/views/synthetics/components/agentSelector/AgentSelectorFormBody';
import AgentSelectorDisplay from 'app/views/synthetics/components/agentSelector/AgentSelectorDisplay';
import { LabelMultiSelect } from 'app/components/labels/LabelMultiSelect';

const IP_CONFIG_FILTER = {
  ALL: 'all',
  STATIC: 'static'
};

const fields = {
  agentFamily: {
    placeholder: 'Filter IP Version...',
    options: [
      { value: 'DUAL', label: 'v4 + v6' },
      { value: 'v4', label: 'v4 only' }
    ]
  },
  agentLabelsFilter: {
    placeholder: 'Filter agents by label...'
  },
  searchInputValue: {
    placeholder: 'Search...',
    defaultValue: ''
  },
  staticIpFilter: {
    placeholder: 'Filter IP config...',
    defaultValue: IP_CONFIG_FILTER.ALL,
    options: [
      { value: IP_CONFIG_FILTER.ALL, label: 'All IP Config' },
      { value: IP_CONFIG_FILTER.STATIC, label: 'Static IP Only' }
    ]
  }
};

// Note: do not use @observer here because it won't hear about normal state variable changes and it's not dealing with observables anyway
@inject('$labels')
class AgentSelectorDialog extends Component {
  state = {
    agentLabelsFilter: [],
    searchInputValue: '',
    staticIpFilter: IP_CONFIG_FILTER.ALL
  };

  constructor(props) {
    super(props);
    const { agentFamilyFilter, options } = props;
    const agentFamily = agentFamilyFilter ? [agentFamilyFilter] : [];
    fields.agentFamily.defaultValue = agentFamily;
    // let users filter by ipV6 if a private agent only supports ipv6
    if (
      options.some((option) => option.agent_family === 'v6') &&
      !fields.agentFamily.options.find((option) => option.value === 'v6')
    ) {
      fields.agentFamily.options.push({ value: 'v6', label: 'v6 only' });
    }
    this.state.agentFamily = agentFamily;
  }

  handleFormChange = (data) => {
    const { agentLabelsFilter, searchInputValue, agentFamily, staticIpFilter } = data.formValues;
    this.setState({ agentLabelsFilter, searchInputValue, agentFamily, staticIpFilter });
  };

  get hasStaticIpOptions() {
    const { options } = this.props;
    return options.some((option) => option.capabilities?.includes(AGENT_CAPABILITIES.STATIC_IP));
  }

  get matchingOptions() {
    const { $labels, options } = this.props;
    const { agentLabelsFilter, searchInputValue, agentFamily, staticIpFilter } = this.state;
    const agentLabelsFilterLen = agentLabelsFilter.length;
    const agentFamilyLen = agentFamily.length;

    if (
      agentLabelsFilterLen === 0 &&
      !searchInputValue &&
      agentFamilyLen === 0 &&
      staticIpFilter === IP_CONFIG_FILTER.ALL
    ) {
      return options;
    }

    let matchingOptions =
      agentLabelsFilterLen === 0
        ? options
        : options.filter((option) => $labels.labelMultiSelectModelFilter(option.labels, agentLabelsFilter));

    matchingOptions =
      agentFamilyLen === 0
        ? matchingOptions
        : matchingOptions.filter((option) => agentFamily.includes(option.agent_family));

    matchingOptions =
      staticIpFilter === IP_CONFIG_FILTER.ALL
        ? matchingOptions
        : matchingOptions.filter((option) => option.capabilities?.includes(AGENT_CAPABILITIES.STATIC_IP));

    return !searchInputValue
      ? matchingOptions
      : matchingOptions.filter((option) => {
          const { label, site_name, as_name, asn, cloud_provider, cloud_region } = option;
          const searchValueLower = searchInputValue.toLowerCase();
          return (
            label.toLowerCase().includes(searchValueLower) ||
            (site_name || '').toLowerCase().includes(searchValueLower) ||
            (asn || '').toString().includes(searchValueLower) ||
            (as_name || '').toLowerCase().includes(searchValueLower) ||
            (cloud_provider || '').includes(searchValueLower) ||
            (cloud_region || '').includes(searchValueLower)
          );
        });
  }

  renderDialogTitle() {
    const { $labels, agentLabels } = this.props;
    const agentLabelsOptions = $labels.getLabelOptions(agentLabels);
    return (
      <Flex justifyContent="space-between" alignItems="center" py="3px">
        <Box flex={1} mr={2}>
          Select Agents
        </Box>
        <Flex alignItems="center" justifyContent="flex-end" py="3px" pr="6px">
          <FormComponent onChange={this.handleFormChange} fields={fields} options={{ name: 'AgentDialogFilters' }}>
            <Field name="agentFamily" showLabel={false} mr={1} mb={0} large>
              <Select menuWidth={160} multi keepOpen toggle />
            </Field>
            {this.hasStaticIpOptions && (
              <Field name="staticIpFilter" showLabel={false} mr={1} mb={0} large>
                <Select menuWidth={130} />
              </Field>
            )}
            <Field name="agentLabelsFilter" showLabel={false} options={agentLabelsOptions} mr={1} mb={0}>
              <LabelMultiSelect menuWidth={220} hideFilter />
            </Field>
            <Field name="searchInputValue" showLabel={false} style={{ marginBottom: '0px' }}>
              <InputGroup leftIcon={<Icon icon="search" style={{ marginTop: '7px' }} />} height="30px" />
            </Field>
          </FormComponent>
        </Flex>
      </Flex>
    );
  }

  render() {
    const {
      agents,
      fieldName,
      hideGlobalAgents,
      hideNetworkAgents,
      hidePrivateAgents,
      showPendingAgents,
      showIdColumn,
      isOpen,
      multi,
      onCancel,
      onChange,
      onRemove,
      onSave,
      options,
      overrideSections
    } = this.props;
    const { searchInputValue } = this.state;

    return (
      <Dialog
        isOpen={isOpen}
        title={this.renderDialogTitle()}
        onClose={onCancel}
        width={850}
        height={550}
        top={50}
        canEscapeKeyClose
      >
        <Dialog.Body p={0} overflow="hidden" display="flex">
          {multi && (
            <Box p={2} borderRight="thin" width={250} overflow="auto">
              <Heading level={6}>Selections</Heading>
              <AgentSelectorDisplay
                onRemove={onRemove}
                options={options}
                emptyText="No agents selected"
                showEmptyText
                multi={multi}
                values={agents}
                fieldName={fieldName}
                useValues
              />
            </Box>
          )}
          <Flex flexDirection="column" overflow="hidden" flex={1}>
            <AgentSelectorFormBody
              agents={agents}
              emptyText={searchInputValue.length > 0 ? 'No matching agents found' : undefined}
              onChange={onChange}
              options={this.matchingOptions}
              allOptions={options}
              hideGlobalAgents={hideGlobalAgents}
              hidePrivateAgents={hidePrivateAgents}
              hideNetworkAgents={hideNetworkAgents}
              showPendingAgents={showPendingAgents}
              showIdColumn={showIdColumn}
              overrideSections={overrideSections}
              multi={multi}
              flex={1}
              overflow="auto"
            />
          </Flex>
        </Dialog.Body>
        <Dialog.Footer mt={0} pt={2} borderTop="thin">
          <Button text="Cancel" mr={1} width={110} onClick={onCancel} />
          <Button text="Save" onClick={onSave} intent="primary" width={110} />
        </Dialog.Footer>
      </Dialog>
    );
  }
}

export default AgentSelectorDialog;
